import { useState, useEffect, useContext } from "react";
import {
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Typography,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import APICaller from "../../../utils/APICaller";
import CustomLoader from "../customLoader";
import TableContent from "../../../components/atoms/TableContent";
import TopContainerHeader from "../../../components/molecules/TopComponentHeader";
import AddSection from "./addSection";
import media from "../../../media";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { fetchPermissionsAsDropdownThunk, fetchSectionsThunk } from "../../../store/thunks/sectionsThunk";

const Sections = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  const currentPath = location.pathname.replace("/", "");
  const [tableForm] = Form.useForm();
    
  const { list, pageInfo, count, loading } = useSelector(
    (state) => state.muSections
  );
    
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: Number(process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE || 50),
  });
  const shouldShowPagination = count > pagination.pageSize;
  const tablePagination = shouldShowPagination
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: count,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        locale: { items_per_page: "" },
        onChange: (page, pageSize) => {
          setPagination({ current: page, pageSize });
        },
      }
    : false;
  
  const [showLoader, setShowLoader] = useState(true);
  const slsRole = secureLocalStorage.getItem("role");
  const slsToken = secureLocalStorage.getItem("accessToken");
  const slsLIUId = secureLocalStorage.getItem("lIUId");
  const [data, setData] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [statuses, setStatuses] = useState([
    { label: "ACTIVE", value: "ACTIVE" },
    { label: "INACTIVE", value: "INACTIVE" },
  ]);
  const [selectionType, setSelectionType] = useState("checkbox");  
  const [editingKey, setEditingKey] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const openAddModal = () => {
    setEditRecord(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  const additionalColumns = [];
  const columns = [
    {
      title: () => {
        return <div className="tableTitle"><img src={media.auditId.svg} alt="icon" />ID</div>;
      },
      dataIndex: "id",

      render: (text) => <span className="rowstyle no-wrap">{text}</span>,
    },
    {
      title: () => {
        return <div className="tableTitle"><img src={media.departmentIcon.svg} alt="icon" />Name</div>;
      },
      dataIndex: "name",
      editable: true,

      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text, row) => <span className="rowstyle no-wrap">{text}</span>,
    },
    {
      title: () => {
        return <div className="tableTitle"><img src={media.departmentStatus.svg} alt="icon" />Icon</div>;
      },
      dataIndex: "icon",
      editable: true,

      render: (text, row) => <span className="rowstyle no-wrap">{text}</span>,
    },
    {
      title: () => {
        return <div className="tableTitle"><img src={media.settingsIcon.svg} alt="icon" />Permissions</div>;
      },
      dataIndex: "permissions",
      editable: true,
      render: (permissionIds = []) => {        
        if (!Array.isArray(permissionIds) || permissionIds.length === 0) {
          return "-";
        }

        const labels = permissionIds
          .map(id => permissions.find(p => p.value === id)?.label)
          .filter(Boolean)
          .join(", ");

        return <span className="rowstyle">{labels}</span>;
      },
    },
    {
      title: () => {
        return <div className="tableTitle"><img src={media.departmentStatus.svg} alt="icon" />Status</div>;
      },
      dataIndex: "status",

      editable: true,
      render: (text, row) => (
        <span
          className={`cusStatus ${
            text?.toLowerCase() === "active" ? "cusActive" : "cusInActive"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: () => {
        return <div className="tableTitle"><img src={media.departmentDate.svg} alt="icon" />Created At</div>;
      },
      dataIndex: "createdAt",
      render: (text) => <span className="rowstyle no-wrap">{text}</span>,
    },
  ];

  additionalColumns.push(
    {
      title: () => {
        return <div className="tableTitle"><img src={media.departmentDate.svg} alt="icon" />Updated At</div>;
      },
      dataIndex: "updatedAt",
    },
    {
      title: () => {
        return <div className="tableTitle"><img src={media.departmentAction.svg} alt="icon" />Action</div>;
      },
      dataIndex: "action",

      render: (_, record) => {
        return (
          <Typography.Link
            disabled={editingKey !== ""}
            onClick={() => openEditModal(record)}
            className="tableBtn bfont"
          >
            <i className="fa fa-pencil-square-o" aria-hidden="true"></i> Edit
          </Typography.Link>
        );
      },
    }
  );
  columns.push(...additionalColumns);
  const mergedColumns =
    columns &&
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          editing: isEditing(record) ? true : undefined,
          cellConfig: {
            inputType: "text",
            dataIndex: col.dataIndex,
            title: typeof col.title === "string" ? col.title : undefined,
            statuses,
            permissions
          },        
        }),
      };
    });

  /*begin::getSectionsList will fetch list of users*/

  // Fetch sections
  useEffect(() => {
    const fetchData = async () => {
      try {
        setShowLoader(true);
        const hasSearchedValue = secureLocalStorage.getItem("hasSearchedValue")
          ? "yes"
          : "";
        const searchKeyword = secureLocalStorage.getItem("searchKeyword") || "";

        await dispatch(
          fetchSectionsThunk({
            searchIn: currentPath,
            callActionFrom: currentPath,
            hasSearchedValue,
            searchKeyword,
            route: currentPath,
            pagination: {
              current: pagination.current,
              page: pagination.current,
              pageSize: pagination.pageSize,
            },
          })
        ).unwrap();
      } catch (err) {
        console.error("Error fetching sections:", err);
      } finally {
        setShowLoader(false);
      }
    };

    fetchData();
  }, [currentPath, pagination.current, pagination.pageSize, dispatch]);

  // Fetch permissions
  const getPermissions = () => {    
    const payload = { route: "permissions", action: "findAll" };
    dispatch(fetchPermissionsAsDropdownThunk(payload))
      .unwrap()
      .then((response) => {
        const temp = [];
        response?.map((permission, index) => {
          temp.push({ label: permission?.name, value: permission?._id });
        });
        setPermissions(temp);
      })
      .catch((error) => {});
  };

  useEffect(() => {    
    getPermissions();
  }, []);

  /*end::getSectionsList will fetch list of users*/

  const isEditing = (record) => record.key === editingKey;
  
  return (
    <>
      <div className="all-content">
        <CustomLoader showLoader={loading} />
        <TopContainerHeader
          sectionTitle="Sections"
          sectionDescription={
            showLoader
              ? "Loading..."
              : `${count || 0} Found`
          }
          hasSectionButton={true}
          sectionButtons={[
            {
              isVisible: true,
              itemType: "add",
              itemName: "Add",
              onClick: openAddModal,
            },
          ]}
          hasFilter={false}
          buttonTitle="Add New"
          FormComponent={AddSection}
          formTitle={editRecord ? "Edit Section" : "Add Section"}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          formProps={{
            editData: editRecord,
            statuses: statuses,
            permissions,
            onSuccess: () => {
              dispatch(
                fetchSectionsThunk({
                  searchIn: currentPath,
                  callActionFrom: currentPath,
                  hasSearchedValue: secureLocalStorage.getItem("hasSearchedValue")
                  ? "yes"
                  : "",
                  searchKeyword: secureLocalStorage.getItem("searchKeyword") || "",
                  route: currentPath,
                  pagination: {
                    current: pagination.current,
                    page: pagination.current,
                    pageSize: pagination.pageSize,
                  },
                })
              );
              setModalOpen(false);
            },
          }}
        />

        <div className="all-table-holder">
          <Form form={tableForm} component={false}>
            <TableContent
              className=""
              rowSelection=""
              rowClassName="editable-row"
              bordered={false}
              columns={mergedColumns}
              dataSource={list}
              pagination={tablePagination}
            />
          </Form>
        </div>
      </div>
    </>
  );
};
export default Sections;
